/**
 * Shared date-range model for analytics dashboards (presets + optional custom [start, end]).
 * Backend resolution: custom dates override period preset.
 */

export type MetricsDateRange =
  | { kind: 'preset'; period: string }
  | { kind: 'custom'; startDate: string; endDate: string }

export const DEFAULT_METRICS_DATE_RANGE: MetricsDateRange = {
  kind: 'preset',
  period: '30d',
}

/** Default window for Agent Process Metrics, RAG Analytics, Monitoring → Token Usage (scoped dashboards). */
export const DEFAULT_SCOPED_ANALYTICS_DATE_RANGE: MetricsDateRange = {
  kind: 'preset',
  period: '7d',
}

/** Preset options for workflow/metrics UIs (matches main backend dashboard `period` query). */
export const METRICS_DATE_RANGE_PRESETS = [
  { value: '1d', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 6 months' },
  { value: '365d', label: 'Last year' },
  { value: 'all', label: 'All time' },
] as const

/** Agentserve RAG analytics (`time_range` query); custom ranges use `start_date` / `end_date`. */
export const RAG_ANALYTICS_DATE_PRESETS = [
  { value: '1d', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 6 months' },
  { value: '365d', label: 'Last year' },
  { value: 'all', label: 'All time' },
] as const

export const DEFAULT_RAG_METRICS_DATE_RANGE: MetricsDateRange = {
  kind: 'preset',
  period: '7d',
}

/** Bucketing for time-series APIs (`granularity` / SQL date_trunc). */
export type AdaptiveChartGranularity = 'hourly' | 'daily' | 'weekly' | 'monthly'

/**
 * Adaptive granularity from inclusive calendar-day span (scoped dashboards).
 * ≤ 1 day → hourly; ≤ ~3 months → daily; ≤ 1 year → weekly; beyond → monthly.
 */
export function adaptiveGranularityForDaySpan(inclusiveDays: number): AdaptiveChartGranularity {
  const n = Math.max(1, Math.floor(inclusiveDays))
  if (n <= 1) return 'hourly'
  if (n <= 92) return 'daily'
  if (n <= 366) return 'weekly'
  return 'monthly'
}

export function adaptiveGranularityForMetricsRange(r: MetricsDateRange): AdaptiveChartGranularity {
  const { startDate, endDate } = metricsRangeToAbsoluteDates(r)
  const span = inclusiveDayCountBetween(startDate, endDate)
  return adaptiveGranularityForDaySpan(span)
}

/** Map UI range to Agentserve RAG analytics query (preset or custom ISO dates). */
export function metricsDateRangeToRagApiParams(r: MetricsDateRange): {
  timeRange: string
  startDate?: string
  endDate?: string
} {
  if (r.kind === 'custom') {
    return { timeRange: 'custom', startDate: r.startDate, endDate: r.endDate }
  }
  return { timeRange: r.period }
}

/** Stable key for React Query / cache identity */
export function metricsDateRangeKey(r: MetricsDateRange): string {
  if (r.kind === 'custom') return `c:${r.startDate}:${r.endDate}`
  return `p:${r.period}`
}

export function metricsDateRangeToQueryParams(
  r: MetricsDateRange,
  options?: { defaultPeriodWhenCustom?: string }
): Record<string, string> {
  const defaultP = options?.defaultPeriodWhenCustom ?? '30d'
  if (r.kind === 'custom') {
    return { period: defaultP, start_date: r.startDate, end_date: r.endDate }
  }
  return { period: r.period }
}

/**
 * Params for APIs that share backend `resolve_metrics_window`.
 * Preset `1d` stays `period=1d` (rolling 24h) — never calendar `start_date`/`end_date`.
 */
export function metricsDateRangeToWindowQueryParams(r: MetricsDateRange): {
  period?: string
  start_date?: string
  end_date?: string
} {
  if (r.kind === 'custom') {
    return { start_date: r.startDate, end_date: r.endDate }
  }
  return { period: r.period }
}

/** Append to URLSearchParams (mutates and returns same instance). */
export function appendMetricsDateRangeToSearchParams(
  params: URLSearchParams,
  r: MetricsDateRange,
  options?: { defaultPeriodWhenCustom?: string }
): URLSearchParams {
  const q = metricsDateRangeToQueryParams(r, options)
  Object.entries(q).forEach(([k, v]) => params.set(k, v))
  return params
}

/**
 * Parse a MetricsDateRange from a URL search string or URLSearchParams object.
 * Returns custom range if `start_date` and `end_date` exist, preset if `period` exists, or null.
 */
export function parseDateRangeFromSearchParams(
  search: string | URLSearchParams
): MetricsDateRange | null {
  if (!search) return null
  const params = typeof search === 'string' ? new URLSearchParams(search) : search
  const startDate = params.get('start_date')
  const endDate = params.get('end_date')
  if (startDate && endDate) {
    return { kind: 'custom', startDate, endDate }
  }
  const period = params.get('period')
  if (period) {
    return { kind: 'preset', period }
  }
  return null
}

/**
 * Compact label for inline use in card/KPI labels.
 * Preset  → "7d" | "30d" | "90d" | "all time"
 * Custom  → "3/1–4/28"
 */
export function dateRangeShortLabel(r: MetricsDateRange): string {
  if (r.kind === 'custom') {
    const fmt = (iso: string) => {
      const parts = iso.split('-')
      return `${Number.parseInt(parts[1])}/${Number.parseInt(parts[2])}`
    }
    return `${fmt(r.startDate)}–${fmt(r.endDate)}`
  }
  if (r.period === 'all') return 'all time'
  return r.period
}

/**
 * Longer label for chart/section titles.
 * Preset  → "last 7 days" | "last 30 days" | "last 90 days" | "all time"
 * Custom  → "3/1/26 – 4/28/26"
 */
export function dateRangeLongLabel(r: MetricsDateRange): string {
  if (r.kind === 'custom') {
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split('-')
      return `${Number.parseInt(m)}/${Number.parseInt(d)}/${y.slice(2)}`
    }
    return `${fmt(r.startDate)} – ${fmt(r.endDate)}`
  }
  if (r.period === 'all') return 'all time'
  const m = /^(\d+)d$/.exec(r.period)
  if (m) return `last ${m[1]} days`
  return `last ${r.period}`
}

export function todayISODate(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Default custom range: last `days` calendar days ending today (inclusive). */
export function defaultCustomRangeDays(days: number): { startDate: string; endDate: string } {
  const end = new Date()
  const start = new Date(end)
  start.setDate(start.getDate() - (days - 1))
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  }
}

/**
 * Resolve any range to concrete ISO start/end (for date inputs / chart span).
 * Matches dashboard preset windows (`7d` / `30d` / `90d` as inclusive calendar days ending today).
 * Preset `1d` is **today's calendar day** here — rolling 24h list/KPI queries must use
 * `metricsDateRangeToWindowQueryParams` (`period=1d`), not these dates.
 * `all` uses a long practical window (5y) for picker bounds.
 */
export function metricsRangeToAbsoluteDates(r: MetricsDateRange): { startDate: string; endDate: string } {
  if (r.kind === 'custom') return { startDate: r.startDate, endDate: r.endDate }
  if (r.period === 'all') return defaultCustomRangeDays(365 * 5)
  const presetDays: Record<string, number> = {
    '1d': 1,
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '180d': 180,
    '365d': 365,
  }
  const days = presetDays[r.period] ?? 30
  return defaultCustomRangeDays(days)
}

function addLocalCalendarDays(isoDate: string, deltaDays: number): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const dt = new Date(y, m - 1, d + deltaDays)
  const yy = dt.getFullYear()
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const dd = String(dt.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/** Inclusive calendar days between two ISO `YYYY-MM-DD` strings (local calendar). */
export function inclusiveDayCountBetween(startIso: string, endIso: string): number {
  const [ys, ms, ds] = startIso.split('-').map(Number)
  const [ye, me, de] = endIso.split('-').map(Number)
  const s = new Date(ys, ms - 1, ds)
  const e = new Date(ye, me - 1, de)
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1)
}

export function metricsDateRangeToAgentRuntimeParams(r: MetricsDateRange): {
  period?: string
  start_date?: string
  end_date?: string
  granularity: AdaptiveChartGranularity
} {
  const granularity = adaptiveGranularityForMetricsRange(r)
  if (r.kind === 'custom') {
    return { start_date: r.startDate, end_date: r.endDate, granularity }
  }
  return { period: r.period, granularity }
}

/** Previous window of the same length immediately before the resolved range (for period-over-period). */
export function priorEqualCalendarRange(r: MetricsDateRange): MetricsDateRange {
  const { startDate, endDate } = metricsRangeToAbsoluteDates(r)
  const n = inclusiveDayCountBetween(startDate, endDate)
  const prevEnd = addLocalCalendarDays(startDate, -1)
  const prevStart = addLocalCalendarDays(prevEnd, -(n - 1))
  return { kind: 'custom', startDate: prevStart, endDate: prevEnd }
}
